/**
 * Types for the Daily Rituals System
 * Morning & Evening routines with education, coaching, and tracking
 */

export type RoutineType = 'morning' | 'evening';

export type ActivityType = 
  | 'breathwork' 
  | 'meditation' 
  | 'journaling' 
  | 'gratitude' 
  | 'movement'
  | 'hydration' 
  | 'intention' 
  | 'visualisation' 
  | 'affirmation' 
  | 'reading'
  | 'reflection' 
  | 'letting_go' 
  | 'planning' 
  | 'wind_down' 
  | 'screen_free';

export interface RitualActivity {
  id: string;
  name: string;
  description: string;
  category: 'morning' | 'evening' | 'both';
  activity_type: ActivityType;
  duration_minutes: number;
  benefits: string[];
  science_explanation: string;
  instructions: string;
  icon_name: string;
  sort_order: number;
  is_default: boolean;
}

export interface UserRitualRoutine {
  id: string;
  user_id: string;
  routine_type: RoutineType;
  name: string;
  target_time: string | null;
  reminder_enabled: boolean;
  reminder_minutes_before: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserRitualItem {
  id: string;
  user_id: string;
  routine_id: string;
  activity_id: string | null;
  custom_activity_name: string | null;
  custom_activity_description: string | null;
  sort_order: number;
  duration_minutes: number;
  is_active: boolean;
  activity?: RitualActivity;
}

export interface DailyRitualLog {
  id: string;
  user_id: string;
  date: string;
  routine_type: RoutineType;
  started_at: string | null;
  completed_at: string | null;
  items_completed: string[]; // Array of item IDs
  total_duration_minutes: number;
  mood_before: number | null;
  mood_after: number | null;
  notes: string | null;
}

export interface TomorrowActions {
  id: string;
  user_id: string;
  created_date: string;
  target_date: string;
  action_1: string | null;
  action_2: string | null;
  action_3: string | null;
  action_1_completed: boolean;
  action_2_completed: boolean;
  action_3_completed: boolean;
  action_1_barrier: string | null;
  action_2_barrier: string | null;
  action_3_barrier: string | null;
  future_self_connection: string | null;
  reflection_notes: string | null;
  reviewed_at: string | null;
}

// Default activities for when database isn't available
export const DEFAULT_MORNING_ACTIVITIES: RitualActivity[] = [
  {
    id: 'morning-breathwork',
    name: 'Morning Breathwork',
    description: 'Start your day with intentional breathing to activate your nervous system',
    category: 'morning',
    activity_type: 'breathwork',
    duration_minutes: 5,
    benefits: ['Increases alertness', 'Reduces morning anxiety', 'Improves focus'],
    science_explanation: 'Deep breathing activates the parasympathetic nervous system while stimulating oxygen flow to the brain, helping you transition from sleep to wakefulness with calm alertness.',
    instructions: '1. Sit comfortably with feet on the ground\n2. Take 5 deep breaths: 4 counts in, hold 4, exhale 6\n3. Notice how your body feels\n4. Set an intention for the day',
    icon_name: 'wind',
    sort_order: 1,
    is_default: true
  },
  {
    id: 'gratitude',
    name: 'Gratitude Practice',
    description: 'Acknowledge 3 things you\'re grateful for to prime your brain for positivity',
    category: 'morning',
    activity_type: 'gratitude',
    duration_minutes: 3,
    benefits: ['Improves mood', 'Increases resilience', 'Enhances sleep quality'],
    science_explanation: 'Gratitude activates the reward center of the brain and increases dopamine and serotonin production, creating a positive feedback loop that affects your entire day.',
    instructions: '1. Close your eyes briefly\n2. Think of 3 specific things you\'re grateful for\n3. Really feel the appreciation in your body\n4. Smile and carry this feeling forward',
    icon_name: 'heart',
    sort_order: 2,
    is_default: true
  },
  {
    id: 'morning-sunlight',
    name: 'Morning Sunlight',
    description: 'Get 10 minutes of natural light to regulate your circadian rhythm',
    category: 'morning',
    activity_type: 'movement',
    duration_minutes: 10,
    benefits: ['Regulates sleep-wake cycle', 'Boosts vitamin D', 'Improves mood'],
    science_explanation: 'Morning light exposure triggers the suppression of melatonin and sets your circadian clock, improving sleep quality that night and boosting cortisol for healthy alertness.',
    instructions: '1. Go outside within 30-60 minutes of waking\n2. Look toward the sky (not directly at sun)\n3. Let light enter your eyes naturally\n4. Combine with a short walk if possible',
    icon_name: 'sun',
    sort_order: 3,
    is_default: true
  },
  {
    id: 'intention',
    name: 'Daily Intention',
    description: 'Set a clear intention that aligns with your future self',
    category: 'morning',
    activity_type: 'intention',
    duration_minutes: 3,
    benefits: ['Increases focus', 'Improves decision-making', 'Aligns actions with values'],
    science_explanation: 'Intention setting activates the prefrontal cortex and creates a cognitive anchor that helps filter decisions throughout the day.',
    instructions: '1. Ask yourself: "What does my future self need from me today?"\n2. Choose ONE word or phrase as your intention\n3. Visualise yourself embodying this intention\n4. Write it down where you\'ll see it',
    icon_name: 'target',
    sort_order: 4,
    is_default: true
  },
  {
    id: 'hydration',
    name: 'Hydration Ritual',
    description: 'Drink a full glass of water to rehydrate after sleep',
    category: 'morning',
    activity_type: 'hydration',
    duration_minutes: 2,
    benefits: ['Boosts metabolism', 'Improves cognitive function', 'Supports detoxification'],
    science_explanation: 'After 7-8 hours without water, your body is mildly dehydrated. Rehydrating first thing supports kidney function and kickstarts metabolism.',
    instructions: '1. Keep a glass of water by your bed\n2. Drink it before reaching for your phone\n3. Add lemon for extra vitamin C (optional)\n4. Feel the water energizing your cells',
    icon_name: 'droplets',
    sort_order: 5,
    is_default: true
  }
];

export const DEFAULT_EVENING_ACTIVITIES: RitualActivity[] = [
  {
    id: 'tomorrow-actions',
    name: 'Tomorrow\'s 3 Actions',
    description: 'Identify 3 small steps that bring you closer to your future self',
    category: 'evening',
    activity_type: 'planning',
    duration_minutes: 5,
    benefits: ['Reduces morning decision fatigue', 'Improves goal achievement', 'Creates accountability'],
    science_explanation: 'Pre-commitment and written goal-setting significantly increases follow-through. By connecting actions to your future self, you activate identity-based motivation.',
    instructions: '1. Review your future self vision\n2. Ask: "What 3 small things can I do tomorrow?"\n3. Write them down - be specific\n4. Visualise completing each one',
    icon_name: 'list-checks',
    sort_order: 1,
    is_default: true
  },
  {
    id: 'letting-go',
    name: 'Letting Go Practice',
    description: 'Release the day\'s tensions and unprocessed emotions',
    category: 'evening',
    activity_type: 'letting_go',
    duration_minutes: 5,
    benefits: ['Reduces rumination', 'Improves sleep quality', 'Prevents emotional buildup'],
    science_explanation: 'Conscious emotional release prevents the accumulation of stress hormones that can disrupt sleep and carry into the next day.',
    instructions: '1. Scan your day for any unfinished feelings\n2. Name what you\'re holding onto\n3. Take a deep breath and consciously release it\n4. Tell yourself: "I did my best today"',
    icon_name: 'cloud',
    sort_order: 2,
    is_default: true
  },
  {
    id: 'reflection',
    name: 'Evening Reflection',
    description: 'Review your day with compassion and extract learning',
    category: 'evening',
    activity_type: 'reflection',
    duration_minutes: 5,
    benefits: ['Increases self-awareness', 'Accelerates personal growth', 'Builds self-compassion'],
    science_explanation: 'Reflective practice strengthens neural pathways associated with learning and emotional regulation.',
    instructions: '1. What went well today? (celebrate wins)\n2. What would I do differently?\n3. What did I learn about myself?\n4. How am I closer to my future self?',
    icon_name: 'book-open',
    sort_order: 3,
    is_default: true
  },
  {
    id: 'wind-down',
    name: 'Wind-Down Breathing',
    description: 'Activate your parasympathetic nervous system for sleep',
    category: 'evening',
    activity_type: 'breathwork',
    duration_minutes: 5,
    benefits: ['Lowers heart rate', 'Reduces cortisol', 'Prepares body for sleep'],
    science_explanation: 'Extended exhales activate the vagus nerve and parasympathetic nervous system, lowering heart rate and signaling to your body that it\'s safe to rest.',
    instructions: '1. Lie down or sit comfortably\n2. Breathe in for 4 counts\n3. Exhale slowly for 8 counts\n4. Repeat 5-10 times until relaxed',
    icon_name: 'moon',
    sort_order: 4,
    is_default: true
  },
  {
    id: 'screen-free',
    name: 'Screen-Free Transition',
    description: 'Create a buffer between screens and sleep',
    category: 'evening',
    activity_type: 'screen_free',
    duration_minutes: 30,
    benefits: ['Reduces blue light exposure', 'Improves melatonin production', 'Better sleep quality'],
    science_explanation: 'Blue light from screens suppresses melatonin production. Creating a screen-free buffer allows your brain to naturally prepare for sleep.',
    instructions: '1. Set a phone bedtime alarm\n2. Place devices in another room\n3. Choose a calming activity: reading, stretching, journaling\n4. Keep your bedroom a screen-free zone',
    icon_name: 'smartphone-off',
    sort_order: 5,
    is_default: true
  }
];
